import { useControlEvents } from '@/modules/agent/hooks/useControlEvents';
import { useAgentSelector } from '@/modules/agent/states';
import { useLoggedInMember } from '@/modules/auth/states';
import UserAvatarGroup from '@/shared/components/avatar-group/userAvatarGroup';
import { useEffect, useRef, useState } from 'react';

// global state to track currently active control
let activeControlId: string | null = null;

export const withControlTracking = (WrappedComponent: React.ComponentType<any>) => {
  return function WithControlTrackingComponent(props: any) {
    const { sessionId, controlId, title } = props;
    const { setSelectedId } = useAgentSelector.useActions();
    const loggedInUser = useLoggedInMember();
    const controlRef = useRef<HTMLDivElement>(null);
    const [isActive, setIsActive] = useState(false);
    
    // Track dropdown state for NextUI dropdown components
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const { emitControlOpened, emitControlClosed } = useControlEvents(
      sessionId,
      controlId,
      loggedInUser?.member_id
    );

    // Handle control opening
    const handleControlOpen = () => {
      if (!isActive) {
        // Close previous active control
        if (activeControlId && activeControlId !== controlId) {
          emitControlClosed();
        }

        setSelectedId(sessionId, controlId);
        activeControlId = controlId;
        setIsActive(true);
        emitControlOpened();
      }
    };

    // Handle control closing
    const handleControlClose = () => {
      if (isActive && activeControlId === controlId) {
        setIsActive(false);
        activeControlId = null;
        emitControlClosed();
      }
    };

    // Handle dropdown open/close events
    const handleDropdownOpenChange = (open: boolean) => {
      if (open && !isDropdownOpen) {
        // Opening dropdown - only open control if not already active
        if (!isActive) {
          handleControlOpen();
        }
        setIsDropdownOpen(true);
      } else if (!open && isDropdownOpen) {
        // Closing dropdown - don't close control if textarea might still be focused
        setIsDropdownOpen(false);
        // Don't automatically close control here for multi-component controls
      }
    };

    // Handle selection change for dropdowns
    const handleSelectionChange = (callback: (value: any) => void, isMultiSelect?: boolean) => {
      return (e: React.ChangeEvent<HTMLSelectElement>) => {
        // Call the original callback
        callback(e.target.value);

        // Only close control for single-select dropdowns
        if (!isMultiSelect) {
          handleControlClose();
          setIsDropdownOpen(false);
        }
      };
    };

    useEffect(() => {
      // Set initial active state based on global activeControlId
      setIsActive(activeControlId === controlId);

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;
        const isNextUIPopover = target.closest('[data-nextui-popper-content="true"]');

        // Only close if:
        // 1. Click is outside component
        // 2. Not clicking on a NextUI popover/dropdown
        // 3. Control is currently active
        // 4. This is the active control
        if (controlRef.current && 
          !controlRef.current.contains(target) &&
          !isNextUIPopover &&
            isActive && 
            activeControlId === controlId) {
          handleControlClose();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        if (isActive && activeControlId === controlId) {
          handleControlClose();
        }
      };
    }, [emitControlClosed, isActive, controlId]);

    const enhancedHeader = (
      <div className="flex justify-between items-center w-full">
        <p className="self-start">{title}</p>
        <UserAvatarGroup sessionId={sessionId} control={controlId} />
      </div>
    );

    return (
      // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
      <div ref={controlRef} onClick={handleControlOpen}>
        <WrappedComponent
          {...props}
          enhancedHeader={enhancedHeader}
          onFocus={handleControlOpen}
          onBlur={(e: React.FocusEvent) => {
            // Only close if focus is leaving the entire component
            if (e && !controlRef.current?.contains(e.relatedTarget)) {
              handleControlClose();
            }
          }}
          onDropdownOpenChange={handleDropdownOpenChange}
          wrapSelectionChange={(callback: (value: string)=> void) => handleSelectionChange(callback, props.selectionMode === "multiple")}
        />
      </div>
    );
  };
};